import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import {
  ActionBar,
  Badge,
  Balance,
  Button,
  Callout,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Combobox,
  EntryCell,
  EntryGrid,
  EntryRow,
  Field,
  GridAction,
  GridInput,
  GridSelect,
  Input,
  type ComboboxOption,
  KeyValue,
  Money,
  PageHeader,
  Select,
  StatTile,
  Textarea,
  Toolbar,
  absDecimal,
  cmpDecimal,
  formatDate,
  formatMoney,
  isNegativeDecimal,
  type EntryColumn,
} from '@biovet/ui';
import { ApiError, useResource, type VoucherDetail, type VoucherListRow } from '../lib/api.js';
// The page rules, the letterhead and the print frame are shared with every other
// printed document. See `printVoucher` below for what the copy of them cost.
import { PAGE, esc, letterhead, send } from '../lib/print.js';
import {
  BRANCHES,
  PARTIES,
  accountLabel,
  accountsOfKind,
  defaultAccountOfKind,
  company,
  companyTimezone,
  entryAccounts,
  findAccount,
  findParty,
  hasSubAccounts,
  subAccountsOf,
  type Account,
} from '../lib/reference-data.js';
import {
  VOUCHER_TYPES,
  type VoucherTypeCode,
  type VoucherTypeConfig,
} from '../lib/voucher-config.js';
import {
  amountFieldFor,
  draftFromLoaded,
  duplicateDraft,
  emptyDraft,
  emptyLine,
  parseAmount,
  postingAccountOf,
  summarise,
  toPayload,
  validate,
  type Currency,
  type FxContext,
  type Issue,
  type Role,
  type VoucherDraft,
  type VoucherLineDraft,
} from '../lib/voucher-model.js';
import {
  deleteVoucher,
  listVouchers,
  loadVoucher,
  peekVoucherNumber,
  saveVoucher,
  saveVoucherBatch,
  type SaveStatus,
} from '../lib/voucher-store.js';
import {
  downloadText,
  parseRows,
  readSpreadsheet,
  templateCsv,
  type BulkParse,
} from '../lib/voucher-bulk.js';
import type { BulkResult } from '../lib/api.js';

/**
 * Data entry for the five finance vouchers: JV, CP, CR, BP and BR.
 *
 * One screen driven by `VOUCHER_TYPES`, because the differences between them are
 * data and the rules they share are not. Everything the screen refuses to post
 * is refused by `0015_invariants.sql` too; the validation panel names the trigger
 * beside each message so a rejection here and a rejection at `COMMIT` read as
 * the same rule rather than two.
 *
 * A voucher is a date, a narration, one party and its lines. Currency, rate and
 * branch come from the top bar — they are settings, not fields. Cost centre,
 * zone, business line, rate type and reference are gone from the schema
 * (`0019_simplify_ledger.sql`) and so is the instrument block (`0020`), so the
 * form is not hiding columns; there are none.
 *
 * **Saving writes to PostgreSQL.** `Post` opens a SERIALIZABLE transaction,
 * consumes a number from the gapless series and inserts the header and its
 * lines; the invariant triggers decide whether it commits. What this screen
 * validates first is a courtesy, so an accountant learns about a closed period
 * before typing forty lines — the database remains the authority, and its
 * refusal message is shown verbatim when the two disagree.
 */
export function VoucherEntryScreen({
  type,
  role,
  currency,
  fxRate,
  baseCurrency,
  branchCode,
  accounts,
}: {
  type: VoucherTypeCode;
  role: Role;
  currency: Currency;
  fxRate: string;
  /** The book's reporting currency. Every "is this foreign?" test pivots on it. */
  baseCurrency: Currency;
  branchCode: string;
  /** The chart of accounts, from the API. Empty until it loads. */
  accounts: Account[];
}) {
  const config = VOUCHER_TYPES[type];
  const fx = useMemo<FxContext>(
    () => ({ currency, fxRate, baseCurrency }),
    [currency, fxRate, baseCurrency],
  );
  /** True when the entry currency is not the one the books are kept in. */
  const foreign = currency !== baseCurrency;

  /**
   * Built once per chart, not once per line.
   *
   * A voucher has up to forty rows and the chart has thousands of accounts;
   * rebuilding and re-sorting this list inside the row map would do that work
   * forty times on every keystroke in an amount cell.
   */
  const accountOptions = useMemo(() => accountOptionsFor(accounts, config), [accounts, config]);

  /**
   * The cash or bank accounts the header offers, on a type that offers them.
   *
   * Empty for CP, CR and JV — the first two state their till rather than asking
   * and the third has no contra at all — so the picker below never renders for
   * them. `accounts` is in the dependency list for the same reason it is on
   * `accountOptions`: the registry behind `accountsOfKind()` is filled during
   * `App`'s render, and this has to recompute when it arrives.
   */
  const contraOptions = useMemo<ComboboxOption[]>(() => {
    const kind = config.contra?.pickable ? config.contra.kind : null;
    if (!kind || accounts.length === 0) return [];
    return accountsOfKind(kind).map((account) => ({
      value: account.code,
      label: `${account.code} · ${account.name}`,
      // The balance is the answer to "is there anything in this account to pay
      // from", which is the question somebody choosing between three banks is
      // actually asking.
      hint: account.fcyCurrency
        ? `${account.fcyCurrency} · ${formatMoney(account.balanceBase)}`
        : formatMoney(account.balanceBase),
    }));
  }, [accounts, config]);

  /**
   * A blank voucher of this type, with its cash or bank account already on it.
   *
   * `1111 Cash in Hand` for a cash voucher and `1121 Meezan Bank` for a bank
   * one: this book has one of each, so the header was asking a question with a
   * single answer on every voucher anybody ever entered. The field is gone from
   * the header and the account is shown in the total row and on the printed
   * document, where it is a statement of what posted rather than a control.
   */
  const newDraft = useCallback(
    (): VoucherDraft => ({
      ...emptyDraft(),
      // Empty on a cold load, and the effect below is what fills it. This runs at
      // mount, which on a deep link to /finance/cp is before `GET /api/accounts`
      // has answered — the trap `App` documents around the chart registry and
      // `useDefaultUnits` documents around units.
      contraAccountCode: config.contra ? defaultAccountOfKind(config.contra.kind) : '',
    }),
    [config],
  );

  const [draft, setDraft] = useState<VoucherDraft>(newDraft);
  const [busy, setBusy] = useState<Busy>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appending, setAppending] = useState(false);
  /** Lines that have already reached the ledger. Invariant 3: they are immutable. */
  const [postedLines, setPostedLines] = useState<ReadonlySet<string>>(new Set());
  /**
   * The voucher as the ledger holds it — the audit trail, mostly.
   *
   * Who raised it and who last touched it are not derivable from the draft:
   * they are columns on `vouchers` that only the API can answer for. Kept beside
   * the draft rather than folded into it because they are facts *about* the
   * record, and a draft nobody has saved has none of them.
   */
  const [record, setRecord] = useState<VoucherDetail | null>(null);
  const [focusLine, setFocusLine] = useState<string | null>(null);
  const accountRefs = useRef(new Map<string, HTMLInputElement | null>());

  /** The register this voucher type's navigator walks, newest first. */
  const [register, setRegister] = useState<VoucherListRow[]>([]);
  const [registerOpen, setRegisterOpen] = useState(false);
  /** Voucher-number search, in the record bar. */
  const [search, setSearch] = useState('');
  const [nextNumber, setNextNumber] = useState('…');

  /** Closed on arrival — the work is in the line grid, not the header. */
  const [headerOpen, setHeaderOpen] = useState(false);

  const [bulk, setBulk] = useState<BulkParse | null>(null);
  const [bulkFile, setBulkFile] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const summary = useMemo(() => summarise(draft, config, fx), [draft, config, fx]);
  const validation = useMemo(
    () => validate(draft, config, summary, role, fx),
    [draft, config, summary, role, fx],
  );

  /**
   * The live balance of every account currently on this voucher.
   *
   * The Balance column used to render `accounts.balanceBase` — the roll-up as
   * `GET /api/accounts` returned it when the chart was last fetched, which on a
   * screen left open all morning is the balance as of sign-in. Neither this
   * user's postings nor a colleague's moved it, and the only way to catch up was
   * to reload the page.
   *
   * Keyed on the sorted, de-duplicated set of codes so that re-typing an amount,
   * reordering rows, or picking an account a second line already uses does not
   * refetch: `useResource` compares its params by value, so the request fires
   * exactly when the *set of accounts* changes.
   */
  const balanceCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const line of draft.lines) {
      const code = postingAccountOf(line);
      if (code) codes.add(code);
    }
    if (draft.contraAccountCode) codes.add(draft.contraAccountCode);
    return [...codes].sort();
  }, [draft.lines, draft.contraAccountCode]);

  const balances = useResource<{ balances: Record<string, { balanceBase: string }> }>(
    balanceCodes.length > 0 ? '/account-balances' : null,
    { codes: balanceCodes.join(',') },
  );
  const balanceOf = (code: string): string | undefined =>
    balances.data?.balances[code]?.balanceBase;

  const posted = draft.status === 'POSTED';
  const amountField = amountFieldFor(config);
  /** The header of a posted voucher is frozen: 0015 refuses to rewrite one. */
  const headerLocked = posted;
  /** A posted line can never be edited. A new line may still be appended while
   *  the period is open, because an append is not an amendment — §3.1. */
  const lineLocked = (line: VoucherLineDraft) => posted && postedLines.has(line.id);
  const canAddLines = !posted || appending;

  useEffect(() => {
    if (!focusLine) return;
    accountRefs.current.get(focusLine)?.focus();
    setFocusLine(null);
  }, [focusLine]);

  /**
   * The cash or bank account, once the chart says which one that is.
   *
   * `accounts` is in the dependency list and that is the whole point of the
   * effect. The default is read out of the chart registry now rather than
   * written into `voucher-config.ts` as a literal, and that registry is filled
   * during `App`'s render — so a draft created at mount has an empty contra and
   * nothing but a change to `accounts` can tell this when to fill it.
   *
   * Written only when the field is empty, so it can never overwrite what a saved
   * voucher actually posted to.
   */
  useEffect(() => {
    if (!config.contra || accounts.length === 0) return;
    const fallback = defaultAccountOfKind(config.contra.kind);
    if (!fallback) return;
    setDraft((d) => (d.contraAccountCode ? d : { ...d, contraAccountCode: fallback }));
  }, [accounts, config]);

  /**
   * Header fields, so a collapsed panel can say how many of them are wrong.
   *
   * The contra account counts only where the header actually asks for it — on
   * BP and BR, which offer the bank list. On CP and CR it is fixed by the type
   * and shown in the total row instead, so a badge pointing at a panel with no
   * such field in it would send somebody looking for a control that is not
   * there. The rule against it is still judged and still reported: it reaches
   * the validation panel either way.
   */
  const HEADER_FIELDS = config.contra?.pickable
    ? ['voucherDate', 'fxRate', 'contraAccountCode']
    : ['voucherDate', 'fxRate'];
  const headerErrorCount = validation.errors.filter(
    (issue) => issue.field && HEADER_FIELDS.includes(issue.field),
  ).length;
  const headerHasError = headerErrorCount > 0;

  /**
   * A collapsed panel must never be the reason someone cannot find the problem.
   *
   * Opens itself the moment a header field is in error and leaves itself open —
   * closing it again the instant they fix the date would yank the panel out from
   * under the cursor.
   */
  useEffect(() => {
    if (headerHasError) setHeaderOpen(true);
  }, [headerHasError]);

  // ── Server state ───────────────────────────────────────────────────────────

  const refreshRegister = useCallback(async () => {
    try {
      setRegister(await listVouchers(type));
    } catch {
      // The register is a convenience. Failing to load it must not take the
      // entry form down with it — the form's own errors are the ones that matter.
      setRegister([]);
    }
  }, [type]);

  const refreshNumber = useCallback(async () => {
    try {
      setNextNumber(await peekVoucherNumber(type));
    } catch {
      setNextNumber('—');
    }
  }, [type]);

  useEffect(() => {
    void refreshRegister();
    void refreshNumber();
  }, [refreshRegister, refreshNumber]);

  /**
   * Where the open voucher sits in the register.
   *
   * Derived rather than stored: the register reloads after every write, and a
   * cursor kept in state beside it would be one render away from pointing at the
   * wrong row. −1 means the form holds something the ledger has not seen.
   */
  const position = draft.voucherNo
    ? register.findIndex((row) => row.voucherNo === draft.voucherNo)
    : -1;

  /** Unsaved work worth warning about before it is replaced. */
  const dirty = draft.voucherNo === null && summary.filledLines > 0;

  const discardable = () =>
    !dirty || window.confirm('This voucher has not been saved. Discard it and load another?');

  // ── Navigation ─────────────────────────────────────────────────────────────

  const openVoucher = async (voucherNo: string) => {
    setBusy('load');
    setError(null);
    setFlash(null);
    try {
      const detail = await loadVoucher(type, voucherNo);
      const loaded = draftFromLoaded(detail, config);
      setDraft(loaded);
      setRecord(detail);
      // Everything already in the ledger is frozen. A DRAFT has nothing in it.
      setPostedLines(
        detail.status === 'DRAFT' || detail.status === 'PENDING_APPROVAL'
          ? new Set()
          : new Set(loaded.lines.map((line) => line.id)),
      );
      setAppending(false);
      // The register stays open. Closing it on every selection made the list a
      // one-shot picker — open, choose, reopen — when what somebody comparing
      // four vouchers actually wants is to click down the list and watch the
      // lines below change. Closing it is the chevron's job, and nobody else's.
      if (detail.reversedByNo) {
        setFlash(`${detail.voucherNo} was reversed by ${detail.reversedByNo}.`);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  const goTo = (index: number) => {
    const row = register[index];
    if (!row || !discardable()) return;
    void openVoucher(row.voucherNo);
  };

  // The register is newest first, so "first" is the oldest document — which is
  // what First means to an accountant walking a day book.
  const first = register.length - 1;
  const last = 0;
  const atStart = register.length === 0 || position === first;
  const atEnd = register.length === 0 || position === last;
  const noRecords = register.length === 0;

  /**
   * Find a voucher by number.
   *
   * Matches on a suffix as well as the whole thing, because nobody types
   * `JV-26-000007` when `7` identifies it — and a partial match that finds
   * exactly one voucher is not ambiguous. More than one and it filters the
   * register instead of guessing.
   */
  const searchMatches = search.trim()
    ? register.filter((row) => matchesNumber(row.voucherNo, search))
    : [];

  const runSearch = () => {
    const term = search.trim();
    if (!term) return;
    if (searchMatches.length === 0) {
      setError(`No ${config.label} numbered “${term}”.`);
      return;
    }
    setError(null);
    setRegisterOpen(true);
    // One hit opens it; several leave the filtered list on screen to choose from.
    const only = searchMatches[0];
    if (searchMatches.length === 1 && only && discardable()) void openVoucher(only.voucherNo);
  };

  /**
   * Edit — put the loaded voucher back under the cursor.
   *
   * For a draft that means opening the header, which is where the fields that
   * are not in the grid live. For a posted voucher it means append mode: the
   * ledger is append-only, so the only edit that exists is a new line, and
   * saying "Edit" and then refusing every field would be a lie. The button
   * disables itself on a posted voucher in a closed period, where even an append
   * is refused.
   */
  const editVoucher = () => {
    setHeaderOpen(true);
    if (posted && !appending) {
      setAppending(true);
      addLine();
    }
  };

  // ── Mutation ───────────────────────────────────────────────────────────────

  const patch = (partial: Partial<VoucherDraft>) => setDraft((d) => ({ ...d, ...partial }));

  const patchLine = (id: string, partial: Partial<VoucherLineDraft>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((line) => (line.id === id ? { ...line, ...partial } : line)),
    }));

  const setAmount = (id: string, value: string) =>
    patchLine(id, amountField === 'debit' ? { debit: value } : { credit: value });

  const addLine = (partial: Partial<VoucherLineDraft> = {}) => {
    const line = { ...emptyLine(), ...partial };
    setDraft((d) => ({ ...d, lines: [...d.lines, line] }));
    setFocusLine(line.id);
    return line;
  };

  const removeLine = (id: string) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.length > 1 ? d.lines.filter((line) => line.id !== id) : d.lines,
    }));

  /**
   * Adds the line that closes the difference. The amount goes on the light side,
   * so a voucher 40,000 short of its credits gets a 40,000 credit line.
   */
  const addBalancingLine = () => {
    const difference = summary.differenceTxn;
    if (cmpDecimal(difference, '0') === 0) return;
    const magnitude = absDecimal(difference);
    addLine(isNegativeDecimal(difference) ? { debit: magnitude } : { credit: magnitude });
  };

  const reset = () => {
    if (!discardable()) return;
    setDraft(newDraft());
    setRecord(null);
    setAppending(false);
    setPostedLines(new Set());
    setFlash(null);
    setError(null);
    setRegisterOpen(false);
  };

  const duplicate = () => {
    setDraft(duplicateDraft(draft));
    setRecord(null);
    setAppending(false);
    setPostedLines(new Set());
    setFlash(null);
    setError(null);
  };

  const submitVoucher = async (status: SaveStatus) => {
    setBusy(status === 'POSTED' ? 'post' : status === 'DRAFT' ? 'draft' : 'submit');
    setError(null);
    setFlash(null);
    try {
      const saved = await saveVoucher(toPayload(type, draft, summary, fx, branchCode), status);

      setDraft((d) => ({
        ...d,
        voucherNo: saved.voucherNo,
        status:
          saved.status === 'POSTED'
            ? 'POSTED'
            : saved.status === 'PENDING_APPROVAL'
              ? 'PENDING_APPROVAL'
              : 'DRAFT',
        approvalState: saved.status === 'PENDING_APPROVAL' ? 'PENDING' : 'NOT_REQUIRED',
      }));

      if (saved.status === 'POSTED') {
        setPostedLines(
          new Set(
            summary.entered
              .filter((entry) => entry.debit !== null || entry.credit !== null)
              .map((entry) => entry.line.id),
          ),
        );
      }

      setAppending(false);
      setFlash(
        status === 'DRAFT'
          ? `Saved as draft ${saved.voucherNo}. Nothing has reached the ledger.`
          : status === 'PENDING_APPROVAL'
            ? `${saved.voucherNo} submitted for approval — it sits with a checker until approved.`
            : `${saved.voucherNo} posted. ${saved.lineCount} lines, ${formatMoney(saved.totalDebitBase)} each side.`,
      );

      // The balance column too, and only once the voucher has actually posted.
      // The set of accounts on screen has not changed, so nothing re-keys the
      // balances request — but what those accounts are *worth* just did, and a
      // Balance column still showing the pre-posting figure beside a voucher
      // marked POSTED is precisely the staleness this replaced. A draft moves
      // nothing in the ledger, so it is deliberately left alone.
      await Promise.all([refreshRegister(), refreshNumber()]);
      if (saved.status === 'POSTED') balances.reload();

      // Who raised it and when is the ledger's answer, not one this screen can
      // compose from what it just sent. Failing to read it back leaves the
      // posting summary without an audit trail and the voucher posted, which is
      // the right way round — so this does not go in the same try as the write.
      try {
        setRecord(await loadVoucher(type, saved.voucherNo));
      } catch {
        setRecord(null);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  // ── Keyboard ───────────────────────────────────────────────────────────────

  /**
   * Enter posts. Ctrl-D saves a draft. Alt-A adds a line.
   *
   * Data entry is done from the keyboard by people who do forty vouchers in a
   * sitting, and reaching for the mouse to press Post is the slowest part of
   * that. Enter does exactly what the Post button does — the same guard, the
   * same refusal, no confirmation — because a second key to confirm the first
   * is a key people learn to press without reading.
   *
   * Three things are deliberately not intercepted, and each one is a place
   * Enter already means something:
   *
   * · **A combobox.** The account picker takes Enter to choose the highlighted
   *   row, and it calls `preventDefault` when it does — which is what
   *   `defaultPrevented` below is reading. The voucher-number search does the
   *   same.
   * · **A textarea.** The narration is multi-line; Enter is a newline.
   * · **A focused button or link.** Enter is that control's own activation, and
   *   posting instead of pressing the button under the cursor would be a
   *   keyboard trap.
   *
   * Held in a ref and installed once: the listener is on the document, and
   * re-subscribing on every keystroke in an amount cell — which is what a
   * dependency array over `validation` would mean — is a listener churn nobody
   * needs. The ref always holds this render's closure, so nothing goes stale.
   */
  const shortcuts = useRef<(event: globalThis.KeyboardEvent) => void>(() => {});

  shortcuts.current = (event) => {
    if (busy !== null) return;

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    const typing = tag === 'TEXTAREA';
    const activating = tag === 'BUTTON' || tag === 'A' || tag === 'SELECT';
    /**
     * `'true'` with the list showing, `'false'` on a picker that merely has
     * focus, `null` on anything that is not a combobox.
     *
     * The distinction is the whole reason this is read rather than trusting
     * `defaultPrevented`: `Combobox` calls `preventDefault` on Enter either way
     * — opening its list when shut, choosing a row when open — so a voucher
     * whose last line was added with Alt-A, which leaves the caret in a shut
     * account picker, could never be posted from the keyboard at all.
     */
    const expanded = target?.getAttribute('aria-expanded');

    if (event.altKey || event.ctrlKey || event.metaKey) {
      // A chord nobody below claims is nobody's business but the browser's.
      if (event.defaultPrevented) return;
    }

    // Alt-A — add a line. On a posted voucher that means entering append mode
    // first, which is the only edit an append-only ledger has.
    if (event.altKey && (event.key === 'a' || event.key === 'A')) {
      if (posted && !appending) {
        // The same gate the Edit button uses: even an append is refused once
        // the period is shut.
        if (validation.period && validation.period.status !== 'OPEN') return;
        event.preventDefault();
        setAppending(true);
        addLine();
        return;
      }
      if (!canAddLines) return;
      event.preventDefault();
      addLine();
      return;
    }

    // Ctrl-D / ⌘-D — save a draft. The browser's own bookmark dialog is on this
    // chord, and taking it is the point: there is nothing to bookmark here.
    if ((event.ctrlKey || event.metaKey) && (event.key === 'd' || event.key === 'D')) {
      if (!validation.canSaveDraft) return;
      event.preventDefault();
      void submitVoucher('DRAFT');
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      if (typing || activating) return;
      // The picker is showing its rows: Enter belongs to it.
      if (expanded === 'true') return;
      // Something that is not a combobox already handled Enter — the voucher
      // number search does exactly that, and finding a voucher should not also
      // post the one on screen.
      if (event.defaultPrevented && expanded === null) return;
      if (!validation.canPost) return;
      event.preventDefault();
      void submitVoucher('POSTED');
    }
  };

  useEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => shortcuts.current(event);
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, []);

  /**
   * Delete a draft, or reverse a posted voucher.
   *
   * The button says Delete because that is what the accountant means. What the
   * ledger does depends on the status and is not negotiable — `0015` refuses to
   * delete anything posted — so the confirmation says which of the two is about
   * to happen before it happens, and the result says which one did.
   */
  const removeVoucher = async () => {
    if (!draft.voucherNo) return;
    const isPosted = draft.status === 'POSTED';

    const confirmed = window.confirm(
      isPosted
        ? `${draft.voucherNo} is posted, so it cannot be deleted — the ledger is append-only.\n\nA reversal voucher will be posted instead: the same lines with the sides swapped, dated today. Both documents stay in the ledger and the account balances net to nothing.\n\nContinue?`
        : `Delete draft ${draft.voucherNo}?\n\nIt has not reached the ledger, so nothing is reversed. Its number stays consumed — the series is gapless by design.`,
    );
    if (!confirmed) return;

    let reason: string | undefined;
    if (isPosted) {
      // Cancelling the reason prompt cancels the reversal: somebody who thought
      // better of it half way through should not have it posted anyway.
      const entered = window.prompt('Reason for the reversal (recorded on the voucher):', '');
      if (entered === null) return;
      reason = entered.trim() || undefined;
    }

    setBusy('delete');
    setError(null);
    setFlash(null);
    try {
      const result = await deleteVoucher(type, draft.voucherNo, reason);
      setFlash(result.message);

      await Promise.all([refreshRegister(), refreshNumber()]);

      if (result.action === 'DELETED') {
        setDraft(newDraft());
        setRecord(null);
        setPostedLines(new Set());
      } else {
        // The original is still there, now REVERSED — reload it so the form
        // shows the status the ledger has rather than the one it had.
        await openVoucher(draft.voucherNo);
        setFlash(result.message);
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  // ── Bulk upload ────────────────────────────────────────────────────────────

  const onFileChosen = async (file: File | undefined) => {
    if (!file) return;
    setBusy('bulk');
    setError(null);
    setBulkResult(null);
    try {
      const rows = await readSpreadsheet(file);
      setBulk(parseRows(rows, type, config, { currency, fxRate, baseCurrency, branchCode, role }));
      setBulkFile(file.name);
    } catch (cause) {
      setBulk(null);
      setBulkFile('');
      setError(messageOf(cause));
    } finally {
      setBusy(null);
      // Clearing it means re-choosing the same file after a fix still fires
      // `change`, which it otherwise would not.
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const uploadBulk = async (status: SaveStatus) => {
    if (!bulk) return;
    const ready = bulk.vouchers.filter((v) =>
      status === 'POSTED' ? v.validation.canPost : v.validation.canSaveDraft,
    );
    if (ready.length === 0) return;

    setBusy('bulk');
    setError(null);
    try {
      const result = await saveVoucherBatch(
        ready.map((v) => v.payload),
        status,
      );
      setBulkResult(result);
      await Promise.all([refreshRegister(), refreshNumber()]);
      if (result.failed === 0) {
        setBulk(null);
        setBulkFile('');
      }
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  };

  const bulkPostable = bulk?.vouchers.filter((v) => v.validation.canPost).length ?? 0;
  const bulkBlocked = (bulk?.vouchers.length ?? 0) - bulkPostable;

  // ── Render ─────────────────────────────────────────────────────────────────

  const columns = lineColumns(config, currency, baseCurrency);
  const branchName = BRANCHES.find((b) => b.code === branchCode)?.name ?? branchCode;
  const periodBlocked = validation.period && validation.period.status !== 'OPEN';
  const shownNumber = draft.voucherNo ?? nextNumber;

  return (
    <>
      <PageHeader
        breadcrumb={`Finance & Accounts / ${config.label}`}
        title={`${config.label} · ${shownNumber}`}
        description={config.description}
        meta={
          <>
            <Badge status={posted ? 'POSTED' : draft.status === 'NEW' ? 'DRAFT' : draft.status} />
            <Chip>{branchName}</Chip>
            <Chip>{foreign ? `${currency} @ ${fxRate}` : currency}</Chip>
            {periodBlocked && (
              <Badge tone="warn">
                {validation.period?.name} · {validation.period?.status.toLowerCase()}
              </Badge>
            )}
            {validation.requiresApproval && <Badge tone="warn">Needs a checker</Badge>}
          </>
        }
      />

      {error && (
        <div data-noprint="1" className="pb-3.5">
          <Callout tone="danger" title="The ledger refused this">
            {error}
          </Callout>
        </div>
      )}

      {flash && (
        <div data-noprint="1" className="pb-3.5">
          <Callout tone={posted ? 'success' : 'info'} title={flash}>
            {posted && (
              <span>
                A posted voucher is append-only: its lines cannot be edited or deleted, and a
                correction is a reversal voucher. Appending a line while the period is open is
                allowed — business flow §3.1.
              </span>
            )}
          </Callout>
        </div>
      )}

      {/*
        Post, Save draft and the sentence saying whether either is possible —
        above the document rather than pinned under it.

        It used to be a sticky strip at the foot of the viewport, which put it
        permanently over the last rows of the line grid and the card footer
        below them: on a forty-line voucher the bar was covering the very rows
        somebody was scrolled down to read. It is not sticky now and it does not
        need to be, because it is the first thing on the page: the state of the
        voucher — ready, or the list of what is stopping it — is read before the
        grid rather than hunted for after it, and Post is one place, always the
        same place, whatever the voucher is scrolled to.
      */}
      <ActionBar placement="top">
        <span className="mr-auto text-[11.5px] text-[var(--ink-3)]">
          {validation.canPost
            ? 'Ready to post.'
            : `Cannot post — ${validation.postBlockers.join('; ')}.`}
        </span>

        <Button
          variant="secondary"
          loading={busy === 'draft'}
          disabled={!validation.canSaveDraft || busy !== null}
          onClick={() => void submitVoucher('DRAFT')}
          title="Ctrl-D"
        >
          Save draft
        </Button>

        {validation.requiresApproval && (
          <Button
            variant="secondary"
            loading={busy === 'submit'}
            disabled={!validation.canSubmit || busy !== null}
            onClick={() => void submitVoucher('PENDING_APPROVAL')}
          >
            Submit for approval
          </Button>
        )}

        <Button
          variant="primary"
          loading={busy === 'post'}
          disabled={!validation.canPost || busy !== null}
          onClick={() => void submitVoucher('POSTED')}
          title={validation.canPost ? 'Enter' : validation.postBlockers.join('; ')}
        >
          {posted && appending ? 'Post added lines' : 'Post voucher'}
        </Button>
      </ActionBar>

      <div className="grid gap-3.5 xl:grid-cols-[minmax(0,1fr)_336px]">
        <div className="flex min-w-0 flex-col gap-3.5">
          {/*
            The record bar. Nine actions in the order an accountant reaches for
            them — what to do with this voucher, then how to move between them —
            and a number search, because past a few hundred vouchers Previous is
            not a way to find one.

            It sits inside this column rather than above both, so it is exactly
            as wide as the voucher header it belongs to. Spanning the GL-effect
            panel as well made it look like page chrome; it is document chrome.

            First is the oldest document, because the register is newest first
            and that is what First means to somebody walking a day book.
          */}
          <Card flush data-noprint="1">
            <Toolbar>
              <Button size="sm" variant="secondary" onClick={reset} disabled={busy !== null}>
                New
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={editVoucher}
                disabled={!draft.voucherNo || busy !== null || Boolean(posted && periodBlocked)}
                title={
                  !draft.voucherNo
                    ? 'Nothing loaded to edit'
                    : posted
                      ? 'Posted vouchers are append-only — this adds a line'
                      : 'Open the header and edit this draft'
                }
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void removeVoucher()}
                loading={busy === 'delete'}
                disabled={!draft.voucherNo || busy !== null}
                title={
                  !draft.voucherNo
                    ? 'Save the voucher before it can be deleted'
                    : draft.status === 'POSTED'
                      ? 'Posted — this will post a reversal voucher'
                      : 'Delete this draft'
                }
              >
                {draft.status === 'POSTED' ? 'Delete (reverses)' : 'Delete'}
              </Button>
              <Button
                size="sm"
                variant={registerOpen ? 'primary' : 'ghost'}
                onClick={() => setRegisterOpen((open) => !open)}
                disabled={busy !== null}
                aria-expanded={registerOpen}
                aria-controls="voucher-register"
                trailingIcon={<Chevron open={registerOpen} />}
              >
                View
              </Button>
              {/*
                Print produces the *voucher*, not the screen it is being edited
                on. `window.print()` put the register, the bulk-upload panel and
                the validation card on paper and called it a document; what an
                accountant files is the entry, so this composes one and prints
                that. Posted only — a draft is not a document, and printing one
                puts a piece of paper into the world that the ledger has never
                heard of.
              */}
              <Button
                size="sm"
                variant="ghost"
                disabled={!posted}
                title={posted ? 'Print this voucher' : 'Only a posted voucher can be printed'}
                onClick={() =>
                  printVoucher({
                    config,
                    draft,
                    summary,
                    record,
                    currency,
                    baseCurrency,
                    fxRate,
                  })
                }
              >
                Print
              </Button>

              <span className="mx-1 h-4 w-px bg-[var(--line-2)]" aria-hidden="true" />

              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(first)}
                disabled={noRecords || atStart || busy !== null}
                title="Oldest voucher of this type"
              >
                First
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(position === -1 ? last : position + 1)}
                disabled={noRecords || atStart || busy !== null}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(position === -1 ? last : position - 1)}
                disabled={noRecords || (position !== -1 && atEnd) || busy !== null}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => goTo(last)}
                disabled={noRecords || (position !== -1 && atEnd) || busy !== null}
                title="Most recent voucher of this type"
              >
                Last
              </Button>

              <div className="flex-1" />

              <Input
                size="sm"
                className="w-[160px]"
                placeholder={`Find ${type}-…`}
                aria-label="Search by voucher number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    runSearch();
                  }
                }}
                leading={
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    aria-hidden="true"
                  >
                    <circle cx="6" cy="6" r="4.2" />
                    <path d="M9.2 9.2L12.5 12.5" strokeLinecap="round" />
                  </svg>
                }
              />
              <span className="bb-num shrink-0 text-[11.5px] text-[var(--ink-3)]">
                {noRecords
                  ? 'None yet'
                  : position === -1
                    ? `New · ${register.length}`
                    : `${register.length - position} of ${register.length}`}
              </span>
            </Toolbar>

            {registerOpen && (
              <div
                id="voucher-register"
                className="max-h-[280px] overflow-y-auto border-t border-[var(--line-2)]"
              >
                {(() => {
                  // A search term filters the list rather than replacing it, so
                  // the thing on screen always says which vouchers it is showing.
                  const shown = search.trim() ? searchMatches : register;
                  if (shown.length === 0) {
                    return (
                      <div className="px-[var(--pad)] py-3">
                        <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                          {search.trim()
                            ? `No ${config.label} matches “${search.trim()}”.`
                            : `Nothing posted or drafted for ${config.label} yet.`}
                        </p>
                      </div>
                    );
                  }
                  return shown.map((row) => (
                    <button
                      key={row.voucherNo}
                      type="button"
                      onClick={() => {
                        if (discardable()) void openVoucher(row.voucherNo);
                      }}
                      className={`flex w-full cursor-pointer items-center justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 text-left last:border-b-0 hover:bg-[var(--line-2)]/40 ${
                        row.voucherNo === draft.voucherNo ? 'bg-[var(--accent-soft)]' : ''
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="bb-mono block text-[12px] text-[var(--ink)]">
                          {row.voucherNo}
                        </span>
                        <span className="block truncate text-[11px] text-[var(--ink-3)]">
                          {formatDate(row.voucherDate)} · {row.lineCount} lines
                          {row.partyName ? ` · ${row.partyName}` : ''}
                          {row.narration ? ` · ${row.narration}` : ''}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <Money value={row.totalBase} className="text-[12px]" />
                        <Badge status={row.status} />
                      </span>
                    </button>
                  ));
                })()}
              </div>
            )}
          </Card>

          {/*
            Closed on arrival. The header is three fields that are usually
            already right — today's date, the default cash or bank account — and
            the work is in the grid below it. Opening it is one click; scrolling
            past it was one on every voucher.

            It opens itself when something in it is wrong, because a collapsed
            panel hiding the reason Post is disabled would be worse than the
            scroll it saves.
          */}
          <Card flush>
            <CardHeader
              title="Voucher header"
              subtitle={
                headerOpen
                  ? 'Date and narration. Party belongs on the line that settles against it; currency, rate and branch come from the top bar, the cash or bank account is fixed by the voucher type, and the period is derived from the date.'
                  : headerSummary(draft, config, branchName)
              }
              actions={
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setHeaderOpen((open) => !open)}
                  aria-expanded={headerOpen}
                  aria-controls="voucher-header-body"
                >
                  {headerOpen ? 'Hide' : headerHasError ? 'Fix header' : 'Edit header'}
                  {!headerOpen && headerHasError && <Badge tone="bad">{headerErrorCount}</Badge>}
                </Button>
              }
            />
            {/* Rendered conditionally rather than hidden with `hidden`: the
                attribute's UA `display:none` loses to the `grid` class. */}
            {headerOpen && (
              <CardBody
                id="voucher-header-body"
                className="grid grid-cols-2 gap-x-4 gap-y-3.5 lg:grid-cols-4"
              >
                <Field label="Voucher no." hint="Gapless series, per type and fiscal year">
                  <Input
                    readOnly
                    value={shownNumber}
                    className="bb-mono"
                    aria-label="Voucher number"
                  />
                </Field>

                <Field
                  label="Voucher date"
                  required
                  htmlFor="voucher-date"
                  error={fieldError(validation.issues, 'voucherDate')}
                >
                  <Input
                    id="voucher-date"
                    type="date"
                    value={draft.voucherDate}
                    disabled={headerLocked}
                    invalid={Boolean(fieldError(validation.issues, 'voucherDate'))}
                    onChange={(e) => patch({ voucherDate: e.target.value })}
                  />
                </Field>

                {/*
                  The bank account, on a bank voucher only.

                  A cash voucher's contra is still not here: this book keeps one
                  till, so the field was a question with a single answer, and it
                  is stated in the grid's total row and on the printed document
                  rather than asked. A *bank* voucher is the case that made the
                  removal wrong — there are three bank accounts, and a payment
                  can leave any of them. So BP and BR get the list back, opened
                  on `1121` because that is where most of them go.

                  The options are the chart's, read at render time, so a bank
                  opened in Setup is selectable without a change here. An error
                  against the account still reaches the validation panel as
                  well, which is what keeps a book whose chart carries no bank
                  account saying so rather than posting nowhere.
                */}
                {config.contra?.pickable && (
                  <Field
                    label={config.contra.label}
                    required
                    hint={`The ${config.contra.kind.toLowerCase()} account this voucher's ${config.contra.side === 'Cr' ? 'credit' : 'debit'} lands on`}
                    error={fieldError(validation.issues, 'contraAccountCode')}
                  >
                    <Combobox
                      value={draft.contraAccountCode}
                      disabled={headerLocked}
                      invalid={Boolean(fieldError(validation.issues, 'contraAccountCode'))}
                      options={contraOptions}
                      placeholder="Search code or name…"
                      emptyText={`No ${config.contra.kind.toLowerCase()} account in the chart`}
                      onChange={(code) => patch({ contraAccountCode: code })}
                      aria-label={config.contra.label}
                    />
                  </Field>
                )}

                <Field
                  label="Narration"
                  className="col-span-2 lg:col-span-4"
                  hint={
                    config.contra?.kind === 'BANK'
                      ? 'Heads the printed voucher. Put the cheque or transfer reference here — the General Ledger shows each line’s own narration, not this'
                      : 'Heads the printed voucher. The General Ledger shows each line’s own narration, not this'
                  }
                >
                  <Textarea
                    rows={2}
                    value={draft.narration}
                    disabled={headerLocked}
                    placeholder={config.narrationPlaceholder}
                    onChange={(e) => patch({ narration: e.target.value })}
                    aria-label="Narration"
                  />
                </Field>
              </CardBody>
            )}
          </Card>

          <Card flush>
            <CardHeader
              title={config.shape === 'JOURNAL' ? 'Journal lines' : `${config.amountLabel} lines`}
              subtitle={config.lineHelp}
            />

            <Toolbar>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => addLine()}
                disabled={!canAddLines}
              >
                Add line
              </Button>
              {config.shape === 'JOURNAL' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={addBalancingLine}
                  disabled={!canAddLines || cmpDecimal(summary.differenceTxn, '0') === 0}
                >
                  Add balancing line
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={duplicate} disabled={busy !== null}>
                Duplicate
              </Button>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                Alt-A adds a line · Ctrl-D saves a draft · Enter posts
              </span>
              <div className="flex-1" />
              {summary.balanced ? (
                <Badge tone="ok" dot>
                  Balanced
                </Badge>
              ) : (
                <Badge tone="warn" dot>
                  {formatMoney(absDecimal(summary.differenceBase))} out
                </Badge>
              )}
            </Toolbar>

            <EntryGrid
              aria-label={`${config.label} lines`}
              columns={columns}
              footer={
                <>
                  <EntryRow tone="generated">
                    <EntryCell padded className="text-[var(--ink-3)]">
                      —
                    </EntryCell>
                    <EntryCell padded className="font-semibold">
                      {totalRowLabel(config, draft, currency)}
                    </EntryCell>
                    {/* Sub account, narration and balance: one empty cell each.
                        A short footer row does not centre itself — the browser
                        pads the *end* of it, so a missing cell here slid every
                        total one column to the left of the column it totals. */}
                    <EntryCell />
                    <EntryCell />
                    <EntryCell padded numeric>
                      <LineBalance
                        accountCode={config.shape === 'CONTRA' ? draft.contraAccountCode : ''}
                        balanceBase={balanceOf(draft.contraAccountCode)}
                        baseCurrency={baseCurrency}
                      />
                    </EntryCell>
                    {config.shape === 'JOURNAL' ? (
                      <>
                        <EntryCell padded numeric className="font-semibold">
                          <Money value={summary.totalDebitTxn} currency={currency} dashOnZero />
                        </EntryCell>
                        <EntryCell padded numeric className="font-semibold">
                          <Money value={summary.totalCreditTxn} currency={currency} dashOnZero />
                        </EntryCell>
                      </>
                    ) : (
                      <EntryCell padded numeric className="font-semibold">
                        <Money value={summary.totalDebitTxn} currency={currency} dashOnZero />
                      </EntryCell>
                    )}
                    <EntryCell noPrint />
                  </EntryRow>

                  {foreign && (
                    <EntryRow tone="generated">
                      <EntryCell padded className="text-[var(--ink-3)]">
                        —
                      </EntryCell>
                      <EntryCell padded className="text-[var(--ink-2)]">
                        Base equivalent · {baseCurrency} at {fxRate}
                      </EntryCell>
                      <EntryCell />
                      <EntryCell />
                      <EntryCell />
                      {config.shape === 'JOURNAL' ? (
                        <>
                          <EntryCell padded numeric>
                            <Money value={summary.totalDebitBase} dashOnZero />
                          </EntryCell>
                          <EntryCell padded numeric>
                            <Money value={summary.totalCreditBase} dashOnZero />
                          </EntryCell>
                        </>
                      ) : (
                        <EntryCell padded numeric>
                          <Money value={summary.totalDebitBase} dashOnZero />
                        </EntryCell>
                      )}
                      <EntryCell noPrint />
                    </EntryRow>
                  )}
                </>
              }
            >
              {draft.lines.map((line, index) => {
                const lineIssues = validation.issues.filter((i) => i.lineId === line.id);
                const lineError = lineIssues.some((i) => i.severity === 'error');
                const last = index === draft.lines.length - 1;
                const rowLocked = lineLocked(line);

                return (
                  <EntryRow key={line.id} tone={lineError ? 'invalid' : 'default'}>
                    <EntryCell padded className="text-[11.5px] text-[var(--ink-3)]">
                      {index + 1}
                    </EntryCell>

                    <EntryCell>
                      <Combobox
                        ref={(el) => {
                          accountRefs.current.set(line.id, el);
                        }}
                        value={line.accountCode}
                        disabled={rowLocked}
                        invalid={lineError && !line.accountCode}
                        options={accountOptions}
                        placeholder="Search code or name…"
                        emptyText="No account matches"
                        onChange={(code) =>
                          // Changing the account invalidates whatever was in the
                          // Sub account cell: the old value belonged to a
                          // different head, and leaving it would post a customer
                          // against an expense.
                          patchLine(line.id, {
                            accountCode: code,
                            subAccountCode: '',
                            partyCode: '',
                          })
                        }
                        aria-label={`Line ${index + 1} account`}
                      />
                    </EntryCell>

                    <EntryCell>
                      {/*
                        One column, two questions that are the same question to
                        the person entering a voucher: *which* receivable is
                        this? Where the control account has been broken out into
                        5th-level sub-accounts, the answer is one of them and it
                        is where the line posts. Where it has not, the answer is
                        still a party from the sub-ledger. Where the account
                        keeps no sub-ledger at all there is no question, and the
                        cell says so rather than offering a value `partyFor()`
                        would silently drop.
                      */}
                      {hasSubAccounts(line.accountCode) ? (
                        <Combobox
                          value={line.subAccountCode}
                          disabled={rowLocked}
                          invalid={lineError && !line.subAccountCode}
                          options={subAccountOptionsFor(line.accountCode)}
                          caption={`Sub-accounts of ${accountLabel(line.accountCode)}`}
                          placeholder="Required — search…"
                          emptyText="No sub-account matches"
                          onChange={(code) => patchLine(line.id, { subAccountCode: code })}
                          aria-label={`Line ${index + 1} sub-account`}
                        />
                      ) : keepsSubLedger(line.accountCode) ? (
                        <Combobox
                          value={line.partyCode}
                          disabled={rowLocked}
                          invalid={lineError && !line.partyCode}
                          options={PARTY_OPTIONS}
                          placeholder="Required — search…"
                          emptyText="No party matches"
                          onChange={(code) => patchLine(line.id, { partyCode: code })}
                          aria-label={`Line ${index + 1} sub-account`}
                        />
                      ) : (
                        <span className="px-2 text-[11.5px] text-[var(--ink-3)]">
                          {line.accountCode ? 'No sub-ledger' : '—'}
                        </span>
                      )}
                    </EntryCell>

                    <EntryCell>
                      <GridInput
                        value={line.narration}
                        disabled={rowLocked}
                        placeholder={index === 0 ? config.narrationPlaceholder : 'Line narration'}
                        onChange={(e) => patchLine(line.id, { narration: e.target.value })}
                        aria-label={`Line ${index + 1} narration`}
                      />
                    </EntryCell>

                    {/*
                      What the chosen account already holds.

                      The figure is `GET /api/accounts`'s roll-up in base
                      currency, which is what the ledger held when the chart was
                      last fetched — so it deliberately does *not* move as the
                      amount beside it is typed. This voucher has not posted;
                      showing a balance that already included it would be a
                      figure that reconciles to nothing.

                      It reads the *posting* account, so a line pointed at a
                      control head with a sub-account chosen shows the
                      sub-account's balance rather than the head's total. That
                      is the balance the line is about to move.
                    */}
                    <EntryCell padded numeric>
                      <LineBalance
                        accountCode={postingAccountOf(line)}
                        balanceBase={balanceOf(postingAccountOf(line))}
                        baseCurrency={baseCurrency}
                      />
                    </EntryCell>

                    {config.shape === 'JOURNAL' ? (
                      <>
                        <EntryCell>
                          <GridInput
                            numeric
                            value={line.debit}
                            disabled={rowLocked || parseAmount(line.credit).value !== null}
                            invalid={parseAmount(line.debit).invalid}
                            placeholder="0.00"
                            onChange={(e) => patchLine(line.id, { debit: e.target.value })}
                            aria-label={`Line ${index + 1} debit`}
                          />
                        </EntryCell>
                        <EntryCell>
                          <GridInput
                            numeric
                            value={line.credit}
                            disabled={rowLocked || parseAmount(line.debit).value !== null}
                            invalid={parseAmount(line.credit).invalid}
                            placeholder="0.00"
                            onChange={(e) => patchLine(line.id, { credit: e.target.value })}
                            aria-label={`Line ${index + 1} credit`}
                          />
                        </EntryCell>
                      </>
                    ) : (
                      <EntryCell>
                        <GridInput
                          numeric
                          value={line[amountField]}
                          disabled={rowLocked}
                          invalid={parseAmount(line[amountField]).invalid}
                          placeholder="0.00"
                          onChange={(e) => setAmount(line.id, e.target.value)}
                          aria-label={`Line ${index + 1} amount`}
                        />
                      </EntryCell>
                    )}

                    <EntryCell noPrint>
                      <GridAction
                        label={`Remove line ${index + 1}`}
                        disabled={rowLocked || draft.lines.length === 1}
                        onClick={() => removeLine(line.id)}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M1 1l8 8M9 1L1 9"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </GridAction>
                    </EntryCell>
                  </EntryRow>
                );
              })}
            </EntryGrid>

            <CardFooter>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                {summary.filledLines} of {draft.lines.length} rows carry an amount ·{' '}
                {summary.glLines.length} GL lines will be written
              </span>
              <span className="bb-num text-[11.5px] text-[var(--ink-3)]">
                {currency} · rate {fxRate}
              </span>
            </CardFooter>
          </Card>

          <BulkPanel
            config={config}
            type={type}
            bulk={bulk}
            bulkFile={bulkFile}
            result={bulkResult}
            busy={busy === 'bulk'}
            disabled={busy !== null}
            fileInput={fileInput}
            postable={bulkPostable}
            blocked={bulkBlocked}
            onFile={(file) => void onFileChosen(file)}
            onUpload={(status) => void uploadBulk(status)}
            onClear={() => {
              setBulk(null);
              setBulkFile('');
              setBulkResult(null);
            }}
          />
        </div>

        <div className="flex flex-col gap-3.5">
          <Card flush>
            <CardHeader title="Posting summary" subtitle={`Base currency — ${baseCurrency}`} />
            <CardBody className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <StatTile
                  label="Debit"
                  value={formatMoney(summary.totalDebitBase, { decimals: 0 })}
                  hint={`${summary.glLines.filter((l) => l.side === 'Dr').length} lines`}
                />
                <StatTile
                  label="Credit"
                  value={formatMoney(summary.totalCreditBase, { decimals: 0 })}
                  hint={`${summary.glLines.filter((l) => l.side === 'Cr').length} lines`}
                />
              </div>

              <div
                className={`flex items-center justify-between rounded-[var(--radius)] border px-3 py-2 ${
                  summary.balanced
                    ? 'border-[var(--credit)]/30 bg-[var(--accent-soft)]'
                    : 'border-[var(--warn)]/30 bg-[var(--warn-soft)]'
                }`}
              >
                <span className="text-[12px] font-medium text-[var(--ink-2)]">Difference</span>
                <Money value={summary.differenceBase} colorBySign={!summary.balanced} dashOnZero />
              </div>

              {/*
                The bottom half is the audit trail, and it is only ever the
                ledger's own answer: `record` is what came back from the API,
                so a voucher this screen has not saved shows a dash rather than
                the name of whoever happens to be signed in. Who raised an entry
                is a fact about the record, not about the session looking at it.
              */}
              <KeyValue
                items={[
                  { label: 'Voucher', value: shownNumber, mono: true },
                  { label: 'Date', value: formatDate(draft.voucherDate) },
                  {
                    label: 'Period',
                    value: validation.period
                      ? `${validation.period.name} · ${validation.period.status.toLowerCase()}`
                      : '—',
                  },
                  { label: 'Branch', value: branchName },
                  { label: 'Created by', value: record?.createdByName ?? '—' },
                  { label: 'Last update on', value: stamp(record?.updatedAt) },
                  { label: 'Update by', value: record?.updatedByName ?? '—' },
                  {
                    label: 'Approval',
                    value: `over ${formatMoney(config.approvalThreshold, { decimals: 0 })} needs a checker`,
                  },
                ]}
              />
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader
              title="Validation"
              subtitle={
                validation.errors.length === 0
                  ? 'Every rule the database will apply, applied here first'
                  : `${validation.errors.length} to fix before posting`
              }
              actions={
                validation.errors.length === 0 ? (
                  <Badge tone="ok" dot>
                    Clear
                  </Badge>
                ) : (
                  <Badge tone="bad">{validation.errors.length}</Badge>
                )
              }
            />
            <CardBody className="flex flex-col gap-2">
              {validation.issues.length === 0 ? (
                <p className="m-0 text-[12.5px] text-[var(--ink-2)]">
                  Balanced, in an open period, and every line resolves. Posting is the only thing
                  left.
                </p>
              ) : (
                validation.issues.map((issue, i) => (
                  <div key={i} className="flex gap-2">
                    <span
                      aria-hidden="true"
                      className={`mt-[5px] size-1.5 shrink-0 rounded-full ${
                        issue.severity === 'error' ? 'bg-[var(--debit)]' : 'bg-[var(--warn)]'
                      }`}
                    />
                    <span className="text-[12px] text-[var(--ink-2)]">
                      {issue.lineId && (
                        <span className="text-[var(--ink-3)]">
                          Line {lineNumberOf(draft, issue.lineId)} ·{' '}
                        </span>
                      )}
                      {issue.message}
                      {issue.enforcedBy && (
                        <span className="bb-mono block pt-0.5 text-[10.5px] text-[var(--ink-3)]">
                          {issue.enforcedBy}
                        </span>
                      )}
                    </span>
                  </div>
                ))
              )}
            </CardBody>
          </Card>

          <Card flush>
            <CardHeader
              title="GL effect"
              subtitle={`What reaches voucher_lines${summary.contraSplit ? ' — contra split per line' : ''}`}
            />
            {summary.glLines.length === 0 ? (
              <CardBody>
                <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                  Nothing to post yet — enter an amount.
                </p>
              </CardBody>
            ) : (
              <div className="flex flex-col">
                {summary.glLines.map((line) => (
                  <div
                    key={line.lineNo}
                    className="flex items-center justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 last:border-b-0"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[12px] text-[var(--ink)]">
                        {accountLabel(line.accountCode) || '(no account)'}
                      </span>
                      <span className="block truncate text-[11px] text-[var(--ink-3)]">
                        {line.side === 'Dr' ? 'Debit' : 'Credit'}
                        {line.generated ? ' · generated' : ''}
                        {line.partyCode ? ` · ${partyName(line.partyCode)}` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <Money value={line.amountBase} className="block text-[12px]" />
                      {foreign && (
                        <span className="bb-num block text-[10.5px] text-[var(--ink-3)]">
                          {formatMoney(line.amountTxn, { currency })} {currency}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <p data-report-foot="1" className="hidden pt-4 text-[11px] text-[var(--ink-3)]">
        {company()?.reportFooter ??
          `System-generated from ${company()?.name ?? 'biovet'} Accounting.`}{' '}
        Figures in {baseCurrency} unless stated otherwise.
      </p>
    </>
  );
}

type Busy = 'draft' | 'submit' | 'post' | 'delete' | 'load' | 'bulk' | null;

// ── Bulk upload ──────────────────────────────────────────────────────────────

/**
 * Upload a file of vouchers, see what it will post, then post it.
 *
 * The preview is the point. A bulk import that reports its failures only after
 * writing half the file is how an accountant ends up reconciling a partially
 * imported day, so every row is parsed and run through the same `validate()` the
 * form uses before anything is sent. What reaches the API is the subset that
 * would post; the rest is listed with the reason.
 */
function BulkPanel({
  config,
  type,
  bulk,
  bulkFile,
  result,
  busy,
  disabled,
  fileInput,
  postable,
  blocked,
  onFile,
  onUpload,
  onClear,
}: {
  config: VoucherTypeConfig;
  type: VoucherTypeCode;
  bulk: BulkParse | null;
  bulkFile: string;
  result: BulkResult | null;
  busy: boolean;
  disabled: boolean;
  fileInput: React.RefObject<HTMLInputElement | null>;
  postable: number;
  blocked: number;
  onFile: (file: File | undefined) => void;
  onUpload: (status: SaveStatus) => void;
  onClear: () => void;
}) {
  return (
    <Card flush data-noprint="1">
      <CardHeader
        title="Bulk upload"
        subtitle={`One row per line, grouped by Voucher ref. CSV or .xlsx — every voucher is validated here before any of them is sent.`}
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => downloadText(`${type}-template.csv`, templateCsv(type, config))}
          >
            Download template
          </Button>
        }
      />

      <Toolbar>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="text-[12px] text-[var(--ink-2)] file:mr-2 file:cursor-pointer file:rounded-[var(--radius)] file:border file:border-[var(--line-2)] file:bg-[var(--surface-2)] file:px-2.5 file:py-1 file:text-[12px] file:text-[var(--ink)]"
          disabled={disabled}
          onChange={(e) => onFile(e.target.files?.[0])}
          aria-label={`Upload ${config.label} file`}
        />
        {bulkFile && (
          <>
            <span className="text-[11.5px] text-[var(--ink-3)]">{bulkFile}</span>
            <Button size="sm" variant="ghost" onClick={onClear} disabled={disabled}>
              Clear
            </Button>
          </>
        )}
        <div className="flex-1" />
        {bulk && (
          <>
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              disabled={disabled || bulk.vouchers.length === 0}
              onClick={() => onUpload('DRAFT')}
            >
              Save {bulk.vouchers.length} as drafts
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy}
              disabled={disabled || postable === 0}
              onClick={() => onUpload('POSTED')}
            >
              Post {postable}
            </Button>
          </>
        )}
      </Toolbar>

      {bulk && (
        <div className="flex flex-col">
          {bulk.issues.length > 0 && (
            <div className="border-b border-[var(--line-2)] px-[var(--pad)] py-2">
              {bulk.issues.map((issue, i) => (
                <p key={i} className="m-0 text-[11.5px] text-[var(--ink-3)]">
                  Row {issue.row} · {issue.message}
                </p>
              ))}
            </div>
          )}

          {bulk.vouchers.length === 0 ? (
            <CardBody>
              <p className="m-0 text-[12.5px] text-[var(--ink-3)]">
                No vouchers found in that file. Check that the header row names the columns — the
                template has the ones this voucher type expects.
              </p>
            </CardBody>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              {bulk.vouchers.map((voucher) => {
                const errors = voucher.validation.errors;
                return (
                  <div
                    key={voucher.reference}
                    className="flex items-start justify-between gap-3 border-b border-[var(--line-2)] px-[var(--pad)] py-2 last:border-b-0"
                  >
                    <span className="min-w-0">
                      <span className="block text-[12px] text-[var(--ink)]">
                        <span className="bb-mono">{voucher.reference}</span>
                        <span className="text-[var(--ink-3)]">
                          {' '}
                          · row {voucher.row} · {formatDate(voucher.draft.voucherDate)} ·{' '}
                          {voucher.summary.glLines.length} lines
                        </span>
                      </span>
                      {errors.length > 0 ? (
                        errors.slice(0, 3).map((issue, i) => (
                          <span key={i} className="block text-[11px] text-[var(--debit)]">
                            {issue.message}
                          </span>
                        ))
                      ) : (
                        <span className="block truncate text-[11px] text-[var(--ink-3)]">
                          {voucher.draft.narration || '(no narration)'}
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Money value={voucher.summary.totalDebitBase} className="text-[12px]" />
                      {voucher.validation.canPost ? (
                        <Badge tone="ok" dot>
                          Postable
                        </Badge>
                      ) : (
                        <Badge tone="bad">{errors.length}</Badge>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {blocked > 0 && (
            <CardFooter>
              <span className="text-[11.5px] text-[var(--ink-3)]">
                {blocked} of {bulk.vouchers.length} cannot post yet. Posting sends only the{' '}
                {postable} that can — fix the rest and upload them again.
              </span>
            </CardFooter>
          )}
        </div>
      )}

      {result && (
        <div className="border-t border-[var(--line-2)]">
          <div className="px-[var(--pad)] py-2">
            <Callout
              tone={result.failed === 0 ? 'success' : 'warn'}
              title={`${result.succeeded} of ${result.total} written${result.failed > 0 ? `, ${result.failed} refused` : ''}`}
            >
              {result.failed === 0
                ? 'Every voucher in the file is in the ledger. The register above has them.'
                : 'Each voucher was written in its own transaction, so the ones that succeeded are committed and the ones below changed nothing.'}
            </Callout>
          </div>
          {result.results
            .filter((row) => !row.ok)
            .map((row) => (
              <p
                key={row.row}
                className="m-0 border-t border-[var(--line-2)] px-[var(--pad)] py-1.5 text-[11.5px] text-[var(--ink-2)]"
              >
                <span className="bb-mono">{row.reference}</span> · {row.error}
              </p>
            ))}
        </div>
      )}
    </Card>
  );
}

// ── Printing ─────────────────────────────────────────────────────────────────

/** A timestamp as an accountant reads it, or a dash. */
function stamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: companyTimezone(),
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(at);
}

/** One row of the printed voucher, from the ledger or from the draft. */
interface PrintLine {
  label: string;
  party: string;
  narration: string;
  debit: string;
  credit: string;
}

/**
 * The voucher, on paper.
 *
 * Composed as its own document and printed from a hidden iframe rather than
 * printing the page. The screen is a form — a register, a bulk-upload panel, a
 * validation card, a GL preview — and none of that belongs in a filing cabinet;
 * what does is the entry: who the company is, which voucher this is, what it
 * debited and credited, that the two sides agree, and two signatures.
 *
 * **The page, the letterhead and the frame come from `lib/print.ts`.** They used
 * to be hand-copied here, and the copy had drifted in the two ways a copy always
 * drifts. Its `@page` margin was `16mm` rather than `0`, so Chrome filled the
 * space with its own furniture — `8/25/26, 6:36 AM   JV-26-000001` across the
 * head of every page and `localhost:5173/finance/cp   1/1` across the foot —
 * which is the one thing on a printed voucher that is neither the company's nor
 * the ledger's. And its letterhead read `co.ntn` / `co.strn` straight off the
 * profile, so the three **Print on documents** ticks under Setup → Company
 * Profile governed every other document in the application and were ignored by
 * all five voucher forms. Both are gone; what is left below is the voucher
 * itself.
 *
 * The document is deliberately still composed here rather than through
 * `printDocument()`: its body is a debit/credit table whose whole claim is that
 * the two columns agree, which is not the shape of a trade document, and forcing
 * both through one template would make the template the union of two documents
 * rather than either.
 *
 * An iframe rather than `window.open`, because a popup blocker silently eating
 * a print is indistinguishable from a printer that is off.
 *
 * The rate column appears only on a foreign-currency voucher. On a PKR entry it
 * would be a column of 1s.
 */
function printVoucher({
  config,
  draft,
  summary,
  record,
  currency,
  baseCurrency,
  fxRate,
}: {
  config: VoucherTypeConfig;
  draft: VoucherDraft;
  summary: ReturnType<typeof summarise>;
  record: VoucherDetail | null;
  currency: Currency;
  baseCurrency: Currency;
  fxRate: string;
}) {
  const co = company();
  const foreign = currency !== baseCurrency;

  /**
   * What is printed is what is in the ledger.
   *
   * `record.lines` when there is one — a posted voucher always has one — and the
   * form's own derivation only as a fallback. The two agree on every figure, but
   * not on every blank: a contra line whose analysis line carried a narration
   * shows that narration in the preview and holds none in `voucher_lines`, and a
   * document that fills in a blank the General Ledger has is a document that
   * disagrees with the ledger it was printed from.
   */
  const printed: PrintLine[] = record
    ? record.lines.map((line) => ({
        label: line.accountName ? `${line.accountCode} · ${line.accountName}` : line.accountCode,
        party: line.partyName ?? '',
        narration: line.narration ?? '',
        debit: line.side === 'Dr' ? line.amount : '',
        credit: line.side === 'Cr' ? line.amount : '',
      }))
    : summary.glLines.map((line) => ({
        label: accountLabel(line.accountCode) || line.accountCode,
        party: line.partyCode ? partyName(line.partyCode) : '',
        narration: line.narration,
        debit: line.side === 'Dr' ? line.amountTxn : '',
        credit: line.side === 'Cr' ? line.amountTxn : '',
      }));

  const row = (line: PrintLine) => `
    <tr>
      <td>${esc(line.label)}${line.party ? `<span class="party"> · ${esc(line.party)}</span>` : ''}</td>
      <td>${esc(line.narration)}</td>
      ${foreign ? `<td class="num">${esc(fxRate)}</td>` : ''}
      <td class="num">${line.debit ? formatMoney(line.debit, { currency }) : ''}</td>
      <td class="num">${line.credit ? formatMoney(line.credit, { currency }) : ''}</td>
    </tr>`;

  const doc = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(draft.voucherNo ?? config.label)}</title>
<style>
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 14px 0 8px; text-transform: uppercase; letter-spacing: .08em; }
  .meta { display: flex; gap: 28px; font-size: 11.5px; padding-bottom: 10px; }
  .meta b { font-weight: 600; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #999; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { border-bottom: 1.2px solid #000; font-size: 10.5px; text-transform: uppercase;
       letter-spacing: .06em; }
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td { font-weight: 600; border-top: 1.2px solid #000; border-bottom: 0; }
  .party { color: #555; }
  .sigs { display: flex; gap: 60px; padding-top: 56px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
</style></head>
<body>
  ${letterhead()}

  <h1>${esc(config.label)}</h1>
  <div class="meta">
    <span><b>Voucher no.</b> ${esc(draft.voucherNo ?? '—')}</span>
    <span><b>Date</b> ${esc(formatDate(draft.voucherDate))}</span>
    <span><b>Currency</b> ${esc(currency)}${foreign ? ` @ ${esc(fxRate)}` : ''}</span>
  </div>

  <table>
    <thead>
      <tr>
        <th>Head of account</th>
        <th>Narration</th>
        ${foreign ? '<th class="num">Rate</th>' : ''}
        <th class="num">Debit</th>
        <th class="num">Credit</th>
      </tr>
    </thead>
    <tbody>${printed.map(row).join('')}</tbody>
    <tfoot>
      <tr>
        <td>Total</td>
        <td>${draft.narration ? esc(draft.narration) : ''}</td>
        ${foreign ? '<td></td>' : ''}
        <td class="num">${formatMoney(summary.totalDebitTxn, { currency })}</td>
        <td class="num">${formatMoney(summary.totalCreditTxn, { currency })}</td>
      </tr>
      ${
        foreign
          ? `<tr><td colspan="${3}">Base equivalent · ${esc(baseCurrency)}</td>
             <td class="num">${formatMoney(summary.totalDebitBase)}</td>
             <td class="num">${formatMoney(summary.totalCreditBase)}</td></tr>`
          : ''
      }
    </tfoot>
  </table>

  <div class="sigs">
    <div class="sig">${esc(record?.postedByName ?? record?.createdByName ?? '')}<div class="muted">Prepared by</div></div>
    <div class="sig">&nbsp;<div class="muted">Manager signature</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    ${record?.postedAt ? `Posted ${esc(stamp(record.postedAt))}.` : ''}
    Printed ${esc(stamp(new Date().toISOString()))}.
  </div>
</body></html>`;

  send(doc);
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** An `ApiError` already carries the trigger's sentence and its hint. */
function messageOf(cause: unknown): string {
  if (cause instanceof ApiError) return cause.full;
  if (cause instanceof Error) return cause.message;
  return String(cause);
}

/**
 * One account's standing balance, in the Balance column of a voucher line.
 *
 * Three states, and each one says something different:
 *
 * · **no account chosen** — a dash. There is nothing to be the balance of.
 * · **an account with no postings** — `nil`, in words, rather than `0.00 Cr`.
 *   `Balance` renders a side beside every figure, and a side on nothing is a
 *   claim about which way an empty account leans.
 * · **anything else** — the figure with its Dr or Cr, in base currency.
 *
 * The number comes from `GET /api/account-balances`, refetched whenever the set
 * of accounts on the voucher changes, so it is the ledger's answer now rather
 * than at sign-in. It is still **not** adjusted for the voucher being typed —
 * that voucher has not posted, and a balance that already included it would
 * reconcile to nothing. See the cell that renders this.
 *
 * While the request is in flight the previous figure stays on screen rather than
 * blanking: a column that flickers to a dash on every keystroke elsewhere in the
 * row reads as an error, and the old figure is right until the new one lands.
 */
function LineBalance({
  accountCode,
  balanceBase,
  baseCurrency,
}: {
  accountCode: string;
  balanceBase: string | undefined;
  baseCurrency: Currency;
}) {
  const account = accountCode ? findAccount(accountCode) : undefined;

  if (!account) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">—</span>;
  }
  // Undefined means the balance has not arrived yet — for an account the chart
  // knows about, that is "loading", not "nil". Falling through to `nil` here
  // would state that an account with a balance has none, which is worse than
  // saying nothing.
  if (balanceBase === undefined) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">…</span>;
  }
  if (cmpDecimal(balanceBase || '0', '0') === 0) {
    return <span className="text-[11.5px] text-[var(--ink-3)]">nil</span>;
  }
  return <Balance value={balanceBase} currency={baseCurrency} className="text-[11.5px]" />;
}

/**
 * The Dr/Cr of the cash or bank line is not a choice — a Cash Payment credits
 * cash or it is not a payment. Shown rather than hidden, so the accountant can
 * see the posting the form is about to make, and locked so nobody looks for a
 * way to change it.
 */
function LockedSide({ side }: { side: 'Dr' | 'Cr' }) {
  return (
    <span className="ml-1 inline-flex items-center gap-1 rounded-[var(--radius-pill)] bg-[var(--line-2)] px-1.5 py-[1px] text-[10px] font-medium tracking-normal text-[var(--ink-2)] normal-case">
      <svg width="8" height="9" viewBox="0 0 10 11" fill="none" aria-hidden="true">
        <path
          d="M2.5 4.5V3a2.5 2.5 0 0 1 5 0v1.5"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinecap="round"
        />
        <rect x="1" y="4.5" width="8" height="6" rx="1.4" fill="currentColor" opacity="0.75" />
      </svg>
      {side}
    </span>
  );
}

/** The total row names the generated contra line, so the footer reads as the posting. */
function totalRowLabel(config: VoucherTypeConfig, draft: VoucherDraft, currency: Currency): string {
  if (config.shape === 'JOURNAL') return `Voucher total · ${currency}`;
  const account = draft.contraAccountCode
    ? accountLabel(draft.contraAccountCode)
    : 'no account selected';
  return `${account} · ${config.contra?.side ?? ''}`;
}

/**
 * What the header says about itself while it is shut.
 *
 * Everything that would otherwise need the panel open to check: the date, which
 * cash or bank account the contra line lands on, the party, and the branch. The
 * subtitle slot is one line, so this is deliberately terse — it answers "is this
 * voucher pointed at the right things" and nothing more.
 */
function headerSummary(draft: VoucherDraft, config: VoucherTypeConfig, branchName: string): string {
  const parts = [formatDate(draft.voucherDate)];

  if (config.contra) {
    parts.push(
      draft.contraAccountCode
        ? accountLabel(draft.contraAccountCode)
        : `no ${config.contra.kind.toLowerCase()} account`,
    );
  }

  parts.push(branchName);

  const narration = draft.narration.trim();
  if (narration) parts.push(narration.length > 48 ? `${narration.slice(0, 48)}…` : narration);

  return parts.join(' · ');
}

function fieldError(issues: Issue[], field: string): string | undefined {
  return issues.find((i) => i.field === field && i.severity === 'error')?.message;
}

function lineNumberOf(draft: VoucherDraft, lineId: string): number {
  return draft.lines.findIndex((line) => line.id === lineId) + 1;
}

function partyName(code: string): string {
  return findParty(code)?.name ?? code;
}

/** The View button's disclosure arrow — down when open, right when shut. */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 9 6"
      fill="none"
      aria-hidden="true"
      className="transition-transform duration-[var(--dur-fast)]"
      style={{ transform: open ? 'none' : 'rotate(-90deg)' }}
    >
      <path
        d="M1 1.5L4.5 5L8 1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Does `voucherNo` answer to what was typed?
 *
 * `7`, `000007`, `jv-26-000007` and the whole number all find JV-26-000007.
 * Anything else is a substring match, so a partial series prefix narrows the
 * list rather than finding nothing.
 */
function matchesNumber(voucherNo: string, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return false;
  const hay = voucherNo.toLowerCase();
  if (hay.includes(needle)) return true;
  // A bare number: compare against the sequence, ignoring leading zeros.
  if (/^\d+$/.test(needle)) {
    const tail = hay.split('-').pop() ?? '';
    return Number(tail) === Number(needle);
  }
  return false;
}

/**
 * Does this account keep a sub-ledger?
 *
 * Reads the registry `App` fills from `GET /api/accounts`, so an account marked
 * as a receivable in the chart starts asking for a party with no change here.
 * An unknown or empty code answers no — an empty cell is better than a party
 * picker on a line whose account has not been chosen.
 */
function keepsSubLedger(accountCode: string): boolean {
  return accountCode ? Boolean(findAccount(accountCode)?.requiresParty) : false;
}

function lineColumns(
  config: VoucherTypeConfig,
  currency: Currency,
  baseCurrency: Currency,
): EntryColumn[] {
  const amounts: EntryColumn[] =
    config.shape === 'JOURNAL'
      ? [
          { key: 'debit', label: `Debit ${currency}`, width: '150px', numeric: true },
          { key: 'credit', label: `Credit ${currency}`, width: '150px', numeric: true },
        ]
      : [
          {
            key: 'amount',
            label: `${config.amountLabel} ${currency}`,
            width: '164px',
            numeric: true,
          },
        ];

  return [
    { key: 'no', label: '#', width: '40px' },
    { key: 'account', label: 'Account', required: true },
    // Moved off the header and onto the line, and named for what it now holds:
    // the 5th-level sub-account the line posts to, or — where the control
    // account has not been broken out into sub-accounts — the party from the
    // sub-ledger. One voucher can settle two of them either way.
    { key: 'subAccount', label: 'Sub account', width: '200px' },
    { key: 'narration', label: 'Narration' },
    // What the account already holds, before this voucher. It sits immediately
    // to the left of the amount because that is where it is read: the question
    // it answers — "is there anything in this account, and which way round" —
    // is asked while the amount is being typed, not before or after.
    { key: 'balance', label: `Balance ${baseCurrency}`, width: '160px', numeric: true },
    ...amounts,
    { key: 'actions', label: '', width: '38px', noPrint: true },
  ];
}

/** Which heading an account sits under in the picker. */
function groupOf(account: Account): string {
  switch (account.type) {
    case 'ASSET':
      return '1000 Assets';
    case 'LIABILITY':
      return '2000 Liabilities';
    case 'EQUITY':
      return '3000 Equity';
    case 'INCOME':
      return '4000 Income';
    default:
      return account.code.startsWith('5') ? '5000 Cost of sales' : '6000 Operating expenses';
  }
}

/** Assets before liabilities before equity, as the chart itself is ordered. */
const GROUP_ORDER = [
  '1000 Assets',
  '2000 Liabilities',
  '3000 Equity',
  '4000 Income',
  '5000 Cost of sales',
  '6000 Operating expenses',
];

/**
 * The Account column's rows.
 *
 * `entryAccounts()` decides *what* is offered — leaves, plus the 4th-level heads
 * that hold sub-accounts. This decides how each one reads, and the hint is doing
 * real work: `3 sub-accounts` is the difference between an account that will
 * take the posting and one that is about to ask a second question.
 *
 * A control account on a JV is listed but disabled rather than hidden.
 * `trg_voucher_lines_derive` is what refuses it, and an accountant looking for
 * `1131` on a journal voucher needs to see that it exists and why it is barred —
 * an absent row reads as a missing account.
 *
 * **Unless it has been broken out.** A control account with 5th-level detail
 * takes no line itself: choosing it opens the Sub account column, and what
 * reaches the ledger is `1131-0003`, which is not a control account. So the bar
 * has to lift the moment the head has children, or the fifth level would be
 * unreachable from a JV — exactly the screen where somebody settling one
 * customer against another needs it. `validate()` never had this wrong because
 * it judges `postingAccountOf(line)`; this list judged the head.
 */
function accountOptionsFor(accounts: Account[], config: VoucherTypeConfig): ComboboxOption[] {
  // `accounts` is passed only so this recomputes when the API answers; the
  // registry behind `entryAccounts()` is what actually holds them.
  if (accounts.length === 0) return [];

  return entryAccounts()
    .map((account) => {
      const subs = subAccountsOf(account.code).length;
      const barred =
        config.shape === 'JOURNAL' &&
        Boolean(account.control) &&
        !account.allowDirectPosting &&
        subs === 0;

      return {
        value: account.code,
        label: `${account.code} · ${account.name}`,
        hint: barred
          ? 'control · not on a JV'
          : subs > 0
            ? `${subs} sub-account${subs === 1 ? '' : 's'}`
            : account.fcyCurrency
              ? account.fcyCurrency
              : undefined,
        group: groupOf(account),
        disabled: barred,
      } satisfies ComboboxOption;
    })
    .sort((a, b) => {
      const byGroup = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
      return byGroup !== 0 ? byGroup : a.value.localeCompare(b.value);
    });
}

/**
 * The 5th-level detail under one head, for the Sub account column.
 *
 * `GET /api/accounts` already excludes inactive and deleted rows for the entry
 * screens, so everything here is selectable — there is nothing to disable.
 */
function subAccountOptionsFor(parentCode: string): ComboboxOption[] {
  return subAccountsOf(parentCode).map((account) => ({
    value: account.code,
    label: `${account.code} · ${account.name}`,
  }));
}

/**
 * Parties, for a control account that has no 5th-level sub-accounts yet.
 *
 * This is the older answer to "whose receivable is this", and it stays until
 * every control account has been broken out — a book part-way through that
 * migration has to keep posting.
 */
const PARTY_OPTIONS: ComboboxOption[] = PARTIES.map((party) => ({
  value: party.code,
  label: party.name,
  hint: party.code,
  group:
    party.kind === 'CUSTOMER'
      ? 'Customers'
      : party.kind === 'SUPPLIER'
        ? 'Suppliers'
        : party.kind === 'BROKER_SELLER'
          ? 'Brokerage — Party A'
          : 'Salespersons',
}));
